/**
 * SAP Datasphere OAuth callback landing page.
 *
 * SAP redirects here after user authentication with ?code=...&state=...
 * (or ?error=...&error_description=... on failure).
 *
 * Flow:
 *   1. Read code + state from URL search params.
 *   2. POST to /auth/sap/callback-exchange (backend validates state, exchanges
 *      code for SAP tokens through SapTokenProvider, and upserts the datasource row).
 *   3. On success → navigate back to the surface that started SAP OAuth.
 *   4. On error → Smart Insights / Allocations navigate back with error params;
 *      other callers keep the minimal error page with a return link.
 *
 * Smart Insights keeps final connection updates inside the chat panel, but this
 * callback route still renders a loader while the token exchange runs.
 * The user still holds a valid SmartCHG JWT from the earlier Entra SSO login,
 * so the backend call is authenticated.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    clearSapOAuthReturnRoute,
    getSapOAuthReturnRoute,
    sapOAuthService,
} from '../services/sapOAuthService';

type Status = 'loading' | 'success' | 'error';

export default function SapCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [errorMsg, setErrorMsg] = useState<string>('');
    // Guard against React StrictMode's double-invoke of useEffect: the CSRF
    // state in Redis is single-use, so a second POST would always get 400.
    const calledRef = useRef(false);
    const returnRouteRef = useRef(getSapOAuthReturnRoute());

    const returnToSource = () => {
        const returnRoute = returnRouteRef.current;
        clearSapOAuthReturnRoute();
        navigate(returnRoute, { replace: true });
    };

    const returnToSourceWithParams = (params: Record<string, string>) => {
        const returnRoute = returnRouteRef.current;
        const [path, query = ''] = returnRoute.split('?');
        const nextParams = new URLSearchParams(query);
        Object.entries(params).forEach(([key, value]) => {
            nextParams.set(key, value);
        });
        clearSapOAuthReturnRoute();
        const queryString = nextParams.toString();
        navigate(queryString ? `${path}?${queryString}` : path, { replace: true });
    };

    const shouldReturnResultToSource = () => (
        returnRouteRef.current === '/smart-insights' ||
        returnRouteRef.current === '/allocations' ||
        returnRouteRef.current === '/labor-performance' ||
        returnRouteRef.current === '/smart-morning-for-leaders'
    );

    const handleConnectionError = (code: string, message: string) => {
        if (shouldReturnResultToSource()) {
            returnToSourceWithParams({
                sap_error: code,
                message,
            });
            return;
        }

        setStatus('error');
        setErrorMsg(message);
    };

    useEffect(() => {
        if (calledRef.current) return;
        calledRef.current = true;

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDesc = searchParams.get('error_description');

        if (errorParam) {
            handleConnectionError(errorParam, errorDesc || errorParam);
            return;
        }

        if (!code) {
            handleConnectionError('no_code', 'No authorization code received from SAP.');
            return;
        }

        sapOAuthService
            .exchangeCode(code, state || '', {
                requireConnectedAck: !shouldReturnResultToSource(),
            })
            .then(() => {
                if (shouldReturnResultToSource()) {
                    returnToSourceWithParams({
                        sap_exchange: 'success',
                    });
                    return;
                }
                setStatus('success');
                // Brief pause so the user sees the success state before redirect.
                setTimeout(returnToSource, 1500);
            })
            .catch((err: unknown) => {
                handleConnectionError(
                    'processing_error',
                    err instanceof Error
                        ? err.message
                        : 'SAP connection failed. Please try again.'
                );
            });
    // Only run once on mount — deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (shouldReturnResultToSource()) {
        return <FinanceV2CallbackLoader />;
    }

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Inter, system-ui, sans-serif',
                color: '#5F5F5F',
                fontSize: '0.95rem',
                gap: '1rem',
            }}
        >
            {status === 'loading' && (
                <>
                    <Spinner />
                    <p>Connecting to SAP Datasphere…</p>
                </>
            )}
            {status === 'success' && (
                <>
                    <CheckIcon />
                    <p style={{ color: '#16a34a', fontWeight: 600 }}>
                        SAP Datasphere connected!
                    </p>
                    <p style={{ fontSize: '0.85rem' }}>Redirecting back…</p>
                </>
            )}
            {status === 'error' && (
                <>
                    <ErrorIcon />
                    <p style={{ color: '#dc2626', fontWeight: 600 }}>
                        SAP connection failed
                    </p>
                    {errorMsg && (
                        <p style={{ fontSize: '0.85rem', maxWidth: 400, textAlign: 'center' }}>
                            {errorMsg}
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                        <button
                            onClick={returnToSource}
                            style={{
                                padding: '0.5rem 1.25rem',
                                borderRadius: '0.375rem',
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                            }}
                        >
                            Return to dashboard
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function FinanceV2CallbackLoader() {
    return (
        <div
            className="flex min-h-screen items-center justify-center bg-[#FAF7F1] px-6"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
            <div className="w-full max-w-sm rounded-[28px] bg-white/[0.96] px-8 py-10 text-center shadow-[0_28px_72px_rgba(63,38,48,0.18)] backdrop-blur-sm">
                <div className="mx-auto mb-5 h-12 w-12 animate-spin rounded-full border-4 border-[#8d3165] border-t-transparent" />
                <p className="text-base font-semibold text-[#241c20]">Opening SmartInsights...</p>
            </div>
        </div>
    );
}

function Spinner({ color = '#6366f1', size = 40 }: { color?: string; size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animation: 'spin 1s linear infinite' }}
        >
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#dcfce7" />
            <path
                d="M8 12l3 3 5-5"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ErrorIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#fee2e2" />
            <path
                d="M15 9l-6 6M9 9l6 6"
                stroke="#dc2626"
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
    );
}
